import AgencyHelper from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import DashboardChartCard from 'general/components/quybka/Cards/DashboardChartCard';
import BarChart from 'general/components/quybka/Charts/BarChart';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AgencyDashboardHeaderView from '../../components/AgencyDashboardHeaderView';
import useAppConfig from 'hooks/useAppConfig';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';

GroupDashboardScreen.propTypes = {
  agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function GroupDashboardScreen({ agencyId, ...props }) {
  // MARK: ---- PROPERTIES ----
  const chartRef = useRef();
  const appConfig = useAppConfig();
  const [agencyDetail, setAgencyDetail] = useState(null);
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestAgencyDetail(agencyId).then((response) => {
        setAgencyDetail(response);
      });
    }
  }, [agencyId]);
  // thong tin tru sac
  const [chargePointStatistic, setChargePointStatistic] = useState({
    total: 0,
    charging: 0,
    connected: 0,
  });
  // thong tin he thong
  const [systemStatistic, setSystemStatistic] = useState({
    totalEnergy: 0, // san luong dien trong ngay
    totalPower: 0, // cong suat tuc thi cua he thong
    totalTransactions: 0, // tong so phien sac trong ngay
    totalChargeDuration: 0, // tong thoi gian sac trong ngay
  });
  useEffect(() => {
    requestGetRealtimeData();
    requestGetSystemLogData();
    const timer = setInterval(() => {
      requestGetRealtimeData();
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  const [chartData, setChartData] = useState({});
  const [chartFilter, setChartFilter] = useState({
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    selectedValue: dateFilter.baMuoiNgayGanNhat,
  });
  useEffect(() => {
    ChargeHelper.API.Dashboard.requestGetDailyData({
      from: chartFilter.from,
      to: chartFilter.to,
    }).then((res) => {
      setChartData(res ?? {});
    });
  }, [chartFilter]);
  useEffect(() => {
    setEnergyFilter({ ...chartFilter, energyType: energyType });
    setTransactionFilter({ ...chartFilter, transactionType: transactionType });
  }, [chartFilter])

  const processVehicleData = (data) => {
    if (!data) return;
    const transactionCount = {
      CAR: [],
      BIKE: []
    };
    const totalRevenue = {
      CAR: [],
      BIKE: []
    };
    const totalEnergy = {
      CAR: [],
      BIKE: []
    };

    data?.forEach(day => {
      const date = day.date;

      const carData = day.vehicleTypes.find(v => v.vehicleType === "CAR");
      const bikeData = day.vehicleTypes.find(v => v.vehicleType === "BIKE");

      transactionCount.CAR.push({
        date: date,
        transactions: carData.transactionCount || 0
      });
      transactionCount.BIKE.push({
        date: date,
        transactions: bikeData.transactionCount || 0
      });

      totalRevenue.CAR.push({
        date: date,
        amount: carData.totalRevenue || 0
      });
      totalRevenue.BIKE.push({
        date: date,
        amount: bikeData.totalRevenue || 0
      });

      totalEnergy.CAR.push({
        date: date,
        energy: carData.totalEnergy || 0
      });
      totalEnergy.BIKE.push({
        date: date,
        energy: bikeData.totalEnergy || 0
      });
    });

    return {
      transactionCount,
      totalRevenue,
      totalEnergy
    };
  }

  const chartDataByVehicle = useMemo(() => {
    return processVehicleData(chartData?.dailyByVehicleType);
  }, [chartData])

  const getDataByType = (type, vehicleType, chartData, chartDataByVehicle) => {
    const typeMap = {
      transaction: {
        '': chartData?.transactionByDate,
        BIKE: chartDataByVehicle?.transactionCount?.BIKE,
        CAR: chartDataByVehicle?.transactionCount?.CAR
      },
      energy: {
        '': chartData?.energyByDate,
        BIKE: chartDataByVehicle?.totalEnergy?.BIKE,
        CAR: chartDataByVehicle?.totalEnergy?.CAR
      },
      revenue: {
        '': chartData?.revenueByDate,
        BIKE: chartDataByVehicle?.totalRevenue?.BIKE,
        CAR: chartDataByVehicle?.totalRevenue?.CAR
      }
    };

    return typeMap[type]?.[vehicleType] || null;
  };

  const [energyType, setEnergyType] = useState('');
  const [energyFilter, setEnergyFilter] = useState({ ...chartFilter, energyType: energyType });
  // dien sac theo ngay
  const chartEnergyByDateEl = useMemo(() => {
    const energyByDate = getDataByType('energy', energyType, chartData, chartDataByVehicle);
    if (!energyByDate) return;
    let labels = energyByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = energyByDate?.map((item) => ((item.energy ?? 0) / 1000).toFixed(3));
    if (energyByDate?.length > 31) {
      const groupedData = _.groupBy(energyByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.energy, 0);
        return (total / 1000).toFixed(3);
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê điện sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' kWh'
        }
        valueColor="rgba(41, 132, 238, 1)"
        actionEl={
          <DropdownFilterTable
            id="energyType"
            isLightButton={true}
            toggleElementBorderRadius={10}
            filterComponent={[
              <div className="col-12 p-0">
                <KTDropdownFilter
                  label="Loại điện sạc"
                  filterKey="energyType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  selectedValue={energyFilter.energyType}
                  handleChange={(value) => {
                    setEnergyFilter({ ...energyFilter, energyType: value });
                  }}
                />
              </div>,
              <div className="col-12 p-0">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian thống kê
                </label>
                <CustomDateRangePicker
                  alignRight={true}
                  toggleFontWeight="600"
                  toggleElementBorderRadius={5}
                  initialValue={energyFilter.selectedValue}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setEnergyFilter({
                      ...energyFilter,
                      from: startDate,
                      to: endDate,
                      selectedValue: selectedValue,
                    });
                  }}
                />
              </div>
            ]}
            resetDefaultTable={() => {
              const updateChartFilter = {
                ...chartFilter,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                selectedValue: dateFilter.baMuoiNgayGanNhat,
              };
              setEnergyFilter({ ...updateChartFilter, energyType: '' });
              setEnergyType('');
              setChartFilter(updateChartFilter);
            }}
            saveFilter={() => {
              const { energyType, ...dateFilter } = energyFilter;
              setChartFilter(dateFilter);
              setEnergyType(energyType);
            }}
            isDefaultFilter={Utils.checkDefaultFilter(
              ['selectedValue', 'from', 'to'],
              { ...chartFilter, energyType },
              dateFilter.baMuoiNgayGanNhat,
            )}
          />
        }
        chartEl={
          <BarChart
            title="Điện sạc"
            unit="kWh"
            labels={labels}
            data={data}
            color="rgba(41, 132, 238, 0.4)"
            hoverColor="rgba(41, 132, 238, 1)"
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter, energyFilter, energyType]);
  // doanh thu theo ngay
  const chartRevenueByDateEl = useMemo(() => {
    const revenueByDate = chartData?.revenueByDate ?? [];
    let labels = revenueByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = revenueByDate.map((item) => (item.amount ?? 0) / 1000000);

    if (revenueByDate.length > 31) {
      const groupedData = _.groupBy(revenueByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.amount, 0);
        return (total / 1000000).toFixed(3);
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê doanh thu"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' triệu'
        }
        valueColor="rgba(48, 189, 111, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.selectedValue}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                selectedValue: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Doanh thu"
            unit="triệu"
            labels={labels}
            data={data}
            color="rgba(48, 189, 111, 0.4)"
            hoverColor="rgba(48, 189, 111, 1)"
            showLine={true}
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter]);
  const [transactionType, setTransactionType] = useState('');
  const [transactionFilter, setTransactionFilter] = useState({ ...chartFilter, transactionType: transactionType });
  // phien sac theo ngay
  const chartTransactionByDateEl = useMemo(() => {
    const transactionByDate = getDataByType('transaction', transactionType, chartData, chartDataByVehicle);
    if (!transactionByDate) return;
    let labels = transactionByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = transactionByDate?.map((item) => item.transactions ?? 0);

    if (transactionByDate?.length > 31) {
      const groupedData = _.groupBy(transactionByDate, (item) =>
        moment(item.date).format('MM/YYYY')
      );
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.transactions, 0);
        return total;
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê phiên sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' phiên sạc'
        }
        valueColor="rgba(88, 86, 214, 1)"
        actionEl={
          <DropdownFilterTable
            id="transactionType"
            isLightButton={true}
            toggleElementBorderRadius={10}
            filterComponent={[
              <div className="col-12 p-0">
                <KTDropdownFilter
                  label="Loại phiên sạc"
                  filterKey="transactionType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  selectedValue={transactionFilter.transactionType}
                  handleChange={(value) => {
                    setTransactionFilter({ ...transactionFilter, transactionType: value });
                  }}
                />
              </div>,
              <div className="col-12 p-0">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian thống kê
                </label>
                <CustomDateRangePicker
                  alignRight={true}
                  toggleFontWeight="600"
                  toggleElementBorderRadius={5}
                  initialValue={transactionFilter.selectedValue}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setTransactionFilter({
                      ...transactionFilter,
                      from: startDate,
                      to: endDate,
                      selectedValue: selectedValue,
                    });
                  }}
                />
              </div>
            ]}
            resetDefaultTable={() => {
              const updateChartFilter = {
                ...chartFilter,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                selectedValue: dateFilter.baMuoiNgayGanNhat,
              };
              setTransactionFilter({ ...updateChartFilter, transactionType: '' });
              setTransactionType('');
              setChartFilter(updateChartFilter);
            }}
            saveFilter={() => {
              const { transactionType, ...dateFilter } = transactionFilter;
              setChartFilter(dateFilter);
              setTransactionType(transactionType);
            }}
            isDefaultFilter={Utils.checkDefaultFilter(
              ['selectedValue', 'from', 'to'],
              { ...chartFilter, transactionType },
              dateFilter.baMuoiNgayGanNhat,
            )}
          />
        }
        chartEl={
          <BarChart
            title="Phiên sạc"
            unit="phiên sạc"
            labels={labels}
            data={data}
            color="rgba(88, 86, 214, 0.4)"
            hoverColor="rgba(88, 86, 214, 1)"
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter, transactionFilter, transactionType]);
  // khach hang theo ngay
  const chartActiveUsersByDateEl = useMemo(() => {
    const activeUsersByDate = chartData?.activeUsersByDate ?? [];
    let labels = activeUsersByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = activeUsersByDate.map((item) => item.activeUsers ?? 0);

    if (activeUsersByDate.length > 31) {
      const groupedData = _.groupBy(activeUsersByDate, (item) =>
        moment(item.date).format('MM/YYYY')
      );
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.activeUsers, 0);
        return total;
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê khách hàng"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' khách hàng'
        }
        valueColor="rgba(253, 112, 139, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.selectedValue}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                selectedValue: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Khách hàng"
            unit="khách hàng"
            labels={labels}
            data={data}
            color="rgba(253, 112, 139, 0.4)"
            hoverColor="rgba(253, 112, 139, 1)"
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  // khach hang moi theo ngay
  const chartNewUsersByDateEl = useMemo(() => {
    const newUsersByDate = chartData?.newUsersByDate ?? [];
    let labels = newUsersByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = newUsersByDate?.map((item) => item.newUsers ?? 0);

    if (newUsersByDate?.length > 31) {
      const groupedData = _.groupBy(newUsersByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.newUsers, 0);
        return total;
      });
    }

    return (
      <div ref={chartRef}>
        <DashboardChartCard
          title="Thống kê khách hàng"
          name="Tổng"
          value={
            Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
            ' khách hàng'
          }
          valueColor="rgba(253, 112, 139, 1)"
          actionEl={
            <CustomDateRangePicker
              alignRight={true}
              toggleFontWeight="600"
              toggleElementBorderRadius={10}
              initialValue={chartFilter.selectedValue}
              onPressSave={(selectedValue, startDate, endDate) => {
                setChartFilter({
                  from: startDate,
                  to: endDate,
                  selectedValue: selectedValue,
                });
              }}
            />
          }
          chartEl={
            <BarChart
              title="Khách hàng mới"
              unit="khách hàng"
              labels={labels}
              data={data}
              color="rgba(253, 112, 139, 0.4)"
              hoverColor="rgba(253, 112, 139, 1)"
            />
          }
        />
      </div>
    );
  }, [chartData, chartFilter]);
  // thong tin dau tu
  const [investmentReportData, setInvestmentReportData] = useState({});
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestGetInvestmentReport({
        agencyId,
        date: moment().format('YYYY-MM-DD'),
      }).then((response) => {
        console.log({ response });
        setInvestmentReportData(response);
      });
    }
  }, [agencyId]);
  // thong bao he thong
  const [systemLogList, setSystemLogList] = useState([]);
  const [totalSystemLog, setTotalSystemLog] = useState(0);
  const requestGetSystemLogData = useCallback(() => {
    ChargeHelper.API.SystemLog.requestGetSystemLogList({ page: 0, limit: 10 })
      .then((response) => {
        setSystemLogList(response?.messages);
        setTotalSystemLog(response?.total);
      })
      .finally(() => { });
  }, []);

  const [monthlyGroupCommission, setMonthlyGroupCommission] = useState({});
  useEffect(() => {
    if (!agencyId || !appConfig.groupAgencyId) return;
    ChargeHelper.API.Dashboard.requestGetMonthlyGroupCommission({
      groupAgencyId: appConfig.groupAgencyId,
      month: moment().format('YYYY-MM'),
    }).then((res) => {
      const filterRes = res?.filter((item) => item?.agencyId === agencyId) ?? {};
      setMonthlyGroupCommission(filterRes);
    });
  }, [agencyId, appConfig.groupAgencyId]);

  // MARK: ---- METHODS ----
  const requestGetRealtimeData = useCallback(() => {
    ChargeHelper.API.Dashboard.requestGetRealtimeData().then((res) => {
      setChargePointStatistic({
        total: res?.totalChargePoint ?? 0,
        charging: res?.chargingChargePoint ?? 0,
        connected: res?.connectedChargePoint ?? 0,
      });
      setSystemStatistic({
        totalEnergy: res?.totalEnergy ?? 0,
        totalPower: res?.totalPower ?? 0,
        totalTransactions: res?.totalTransactions ?? 0,
        totalChargeDuration: res?.totalChargeDuration ?? 0,
      });
    });
  }, []);

  return (
    <div className="mt-4" style={{ minHeight: '1200px' }}>
      {/* header */}
      <div className="px-4">
        <AgencyDashboardHeaderView
          name={agencyDetail?.name}
          role="NĐT liên kết"
          contentEl={
            <div className="px-4 d-flex flex-column gap-3 mt-4">
              {UIHelper.buildStatisticItem(
                'rgba(118, 154, 255, 1)',
                <div className="d-flex align-items-center gap-2">Số tiền đã đầu tư</div>,
                `${Utils.formatNumber(Math.round(investmentReportData?.totalAsset)) ?? 0} VNĐ`,
                null,
                'fa-solid fa-chart-line-up'
              )}
              {appConfig.showShareRatio ? UIHelper.buildStatisticItem(
                'rgba(255, 166, 63, 1)',
                <div className="d-flex align-items-center gap-2">
                  Tỷ lệ vốn góp được hưởng, tính đến hiện tại{' '}
                  <KTTooltip
                    text={`<div><span>Tỷ lệ được bắt đầu tính kể từ tháng N+1 (tháng N là tháng NĐT tham gia góp vốn)</span></div>`}
                    textHeader="Tỷ lệ vốn góp được hưởng, tính đến hiện tại"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.primary }}
                    />
                  </KTTooltip>
                </div>,
                `${Utils.formatNumber((investmentReportData?.effectiveShare * 100)?.toFixed(3) ?? 0)}%`,
                null,
                'fa-solid fa-badge-percent'
              ) : <></>}
              {UIHelper.buildStatisticItem(
                '#FD7497',
                <div className="d-flex align-items-center gap-2">
                  Doanh thu dự kiến trong tháng{' '}
                  {/* <KTTooltip
                    text={`<div><span>Tỷ lệ được bắt đầu tính kể từ tháng N+1 (tháng N là tháng NĐT tham gia góp vốn)</span></div>`}
                    textHeader="Tỷ lệ vốn góp được hưởng, tính đến hiện tại"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.primary }}
                    />
                  </KTTooltip> */}
                </div>,
                `${Utils.formatNumber(Math.round(_.sumBy(monthlyGroupCommission, 'energyCommission')) ?? 0)} VNĐ`,
                AppResource.icons.icExpectedRevenue,
                null
              )}
              {UIHelper.buildStatisticItem(
                'rgba(48, 189, 111, 1)',
                'Tổng doanh thu đã nhận',
                `${Utils.formatNumber(investmentReportData?.earned) ?? 0} VNĐ`,
                null,
                'fa-solid fa-chart-mixed'
              )}
            </div>
          }
        />
      </div>

      {/* thong ke */}
      <div className="mt-4">
        <div className="px-4">
          {UIHelper.buildCard(
            <div className="">
              <div className="row m-0 p-0 px-2 pt-4">
                {/* cong suat tuc thi he thong */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    '#00C7BE',
                    'Công suất tức thì của hệ thống',
                    `${Utils.isValid(systemStatistic?.totalPower)
                      ? Utils.formatNumber((systemStatistic?.totalPower / 1000).toFixed(3))
                      : 0
                    } kW`,
                    AppResource.icons.icTotalPower
                  )}
                </div>
                {/* san luong dien trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    '#539FF8',
                    'Tổng sản lượng điện trong ngày',
                    `${Utils.isValid(systemStatistic?.totalEnergy)
                      ? Utils.formatNumber((systemStatistic?.totalEnergy / 1000).toFixed(3))
                      : 0
                    } kWh`,
                    AppResource.icons.icTotalEnergy
                  )}
                </div>
                {/* so phien sac trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    '#7876E5',
                    'Tổng số phiên sạc trong ngày',
                    `${Utils.isValid(systemStatistic?.totalTransactions)
                      ? Utils.formatNumber(systemStatistic?.totalTransactions)
                      : 0
                    } phiên`,
                    AppResource.icons.icTotalTransactions
                  )}
                </div>
                {/* tong thoi gian sac trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    '#7AD661',
                    'Tổng thời gian sạc trong ngày',
                    Utils.isValid(systemStatistic?.totalChargeDuration)
                      ? Utils.convertSecondsToHText(systemStatistic?.totalChargeDuration)
                      : '',
                    AppResource.icons.icTotalDuration
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* bieu do */}
      <div className="mt-4">
        <div className="row m-0 p-0 px-2">
          {/* thong ke dien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartEnergyByDateEl}</div>
          {/* thong ke doanh thu */}
          {/* <div className="col-lg-6 px-2 mb-4">{chartRevenueByDateEl}</div> */}
          {/* thong ke phien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartTransactionByDateEl}</div>
          {/* thong ke khach hang */}
          <div className="col-lg-6 px-2 mb-4">{chartNewUsersByDateEl}</div>
          {/* thong bao */}
          <div className="col-lg-6 px-2 mb-4">
            {UIHelper.buildCard(
              <div style={{ height: `${chartRef?.current?.offsetHeight}px` }}>
                {UIHelper.buildCardHeader(
                  'Thông báo hệ thống',
                  false,
                  null,
                  false,
                  true,
                  <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                    Tổng:{' '}
                    <span style={{ color: AppColors.primary }}>
                      {Utils.formatNumber(totalSystemLog)} thông báo
                    </span>
                  </span>
                )}
                <div className="px-4 mb-4">
                  {UIHelper.buildCard(
                    <div
                      className=""
                      style={{
                        maxHeight: `${chartRef?.current?.offsetHeight - 80}px`,
                        overflowY: 'auto',
                      }}
                    >
                      {systemLogList?.map((item, index) => {
                        return (
                          <div key={item?.messageId ?? index}>
                            {UIHelper.buildCellSystemInfo(
                              <i
                                className={`${ChargeHelper.Utils.getMessageLogTypeIcon(
                                  item?.level
                                )} text-${ChargeHelper.Utils.getMessageLogTypeColor(item?.level)}`}
                                style={{
                                  fontSize: '22px',
                                }}
                              />,
                              item?.title,
                              Utils.formatDateTimeToString(item?.createdAt, 'DD/MM/YYYY - HH:mm'),
                              true,
                              () => { }
                            )}
                          </div>
                        );
                      })}
                    </div>,
                    false,
                    false,
                    false
                  )}
                </div>
              </div>,
              false
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupDashboardScreen;
